import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { LayoutDashboard, LayoutGrid, ClipboardList, ChefHat, Receipt, CalendarDays, UtensilsCrossed, Users, CreditCard, Settings, LogOut, ChevronDown, Menu, FileBarChart, BarChart3, Package, FileText, LifeBuoy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT, LangToggle } from "@/lib/i18n";
import { Logo } from "@/components/Logo";
import { useState } from "react";
import { toast } from "sonner";
import { fmt } from "@/lib/api";
import { useRealtime } from "@/hooks/useRealtime";
import { ClockButton } from "@/pages/app/Shifts";
import { FiscalBanner } from "@/components/FiscalBanner";
import { VoiceAssistant } from "@/components/VoiceAssistant";

const items = [
  ["", "nav_overview", LayoutDashboard, null],
  ["floor", "nav_floor", LayoutGrid, null],
  ["orders", "nav_orders", ClipboardList, null],
  ["kitchen", "nav_kitchen", ChefHat, null],
  ["cashier", "nav_cashier", Receipt, null],
  ["reservations", "nav_reservations", CalendarDays, null],
  ["report", "nav_report", FileBarChart, "owner"],
  ["invoices", "nav_invoices", FileText, "owner"],
  ["monthly", "nav_monthly", BarChart3, "owner"],
  ["menu", "nav_menu", UtensilsCrossed, null],
  ["inventory", "nav_inventory", Package, null],
  ["staff", "nav_staff", Users, "owner"],
  ["subscription", "nav_subscription", CreditCard, "owner"],
  ["settings", "nav_settings", Settings, "owner"],
  ["support", "nav_support", LifeBuoy, null],
];

export default function AppLayout() {
  const { user, logout, restaurants, restaurant, setRestaurant } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  useRealtime(restaurant?.id, (msg) => {
    if (!user) return;
    if (msg.low && user.role !== "staff") toast.warning(`${t("low_stock")}: ${msg.low.map((i) => `${i.name} (${i.qty} ${i.unit})`).join(", ")}`, { duration: 10000 });
    if (msg.online_booking && (user.role === "owner" || user.staff_role === "manager")) toast.info(`${t("new_online_booking")} · ${msg.online_booking.customer_name} · ${msg.online_booking.date} ${msg.online_booking.time} · ${msg.online_booking.guests}p`, { duration: 10000 });
    if (msg.qr_order) toast.info(`${t("new_qr_order")} · ${t("table")} ${msg.qr_order.table_name} · #${msg.qr_order.number} · ${msg.qr_order.items} ${t("dishes_short")}`, { duration: 10000 });
    if (msg.table_call) toast.warning(`${msg.table_call.kind === "bill" ? t("call_bill") : t("call_waiter")} · ${t("table")} ${msg.table_call.table_name}`, { duration: 15000 });
    if (msg.online_payment) toast.success(`${t("online_payment_received")} · ${t("table")} ${msg.online_payment.table_name} · ${fmt(msg.online_payment.amount)}`, { duration: 12000 });
    const r = msg.ready;
    if (!r) return;
    const isWaiter = user.role === "staff" && user.staff_role === "waiter";
    if (isWaiter && r.waiter && r.waiter !== user.name) return;
    if (user.role === "staff" && user.staff_role === "cook") return;
    toast.success(`${t("dish_ready")} · ${t("table")} ${r.table_name}`, { description: r.items.join(", "), icon: <ChefHat size={18} />, duration: 8000 });
  }, ["orders", "stock", "reservations"]);
  if (user === null) return <div className="flex min-h-screen items-center justify-center text-slate-500">...</div>;
  if (user === false) return <Navigate to="/login" replace />;

  const Sidebar = (
    <aside className="flex h-full w-64 flex-col border-r border-slate-100 bg-white">
      <div className="px-6 py-5"><Logo size="text-xl" /></div>
      <div className="px-4">
        <div className="relative">
          <select data-testid="restaurant-switcher" value={restaurant?.id || ""} onChange={(e) => setRestaurant(restaurants.find((r) => r.id === e.target.value))}
            className="w-full appearance-none rounded-xl border border-slate-200 bg-[#f6faf6] px-3 py-2.5 pr-8 text-sm font-semibold text-slate-800 outline-none focus:border-[#5aa469]">
            {restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <ChevronDown size={16} className="pointer-events-none absolute right-3 top-3 text-slate-400" />
        </div>
        <span data-testid="role-badge" className="mt-2 inline-block rounded-full bg-[#5aa469]/10 px-2.5 py-0.5 text-xs font-semibold text-[#4a8a56]">{user.role === "owner" ? t("owner") : `${t("staff")} · ${t(user.staff_role) }`}</span>
        {user.is_admin && <span data-testid="admin-badge" className="ml-1 mt-2 inline-block rounded-full bg-slate-900 px-2.5 py-0.5 text-xs font-semibold text-white">{t("administrator")}</span>}
      </div>
      <nav className="mt-4 flex-1 space-y-0.5 px-3">
        {items.filter(([, , , role]) => !role || user.role === role).map(([path, key, Icon]) => (
          <NavLink key={path} to={`/app/${path}`} end={path === ""} data-testid={`nav-${path || "overview"}`} onClick={() => setOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#5aa469] text-white" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
            <Icon size={18} />{t(key)}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-100 p-4">
        <div className="flex items-center gap-3">
          {user.picture
            ? <img data-testid="user-avatar" src={user.picture} alt={user.name} referrerPolicy="no-referrer" className="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-[#5aa469]/30" />
            : <div data-testid="user-avatar" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#5aa469]/15 font-heading font-bold text-[#4a8a56]">{user.name?.[0]?.toUpperCase()}</div>}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-800" data-testid="user-name">{user.name}</p>
            <p className="truncate text-xs text-slate-500">{user.email}</p>
          </div>
        </div>
        <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} className="mt-3 flex items-center gap-2 text-sm text-slate-600 hover:text-rose-600"><LogOut size={16} />{t("logout")}</button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[#f8fafc]">
      <div className="hidden lg:block">{Sidebar}</div>
      {open && <div className="fixed inset-0 z-40 flex lg:hidden"><div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} /><div className="relative z-10">{Sidebar}</div></div>}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-slate-100 bg-white px-4 py-3 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" data-testid="mobile-menu-btn" onClick={() => setOpen(true)}><Menu size={22} /></button>
            <h2 className="font-heading text-lg font-bold text-slate-900" data-testid="current-restaurant-name">{restaurant?.name}</h2>
          </div>
          <div className="flex items-center gap-2"><VoiceAssistant /><ClockButton /><LangToggle /></div>
        </header>
        <main className="flex-1 overflow-auto p-4 lg:p-8">{!restaurant ? <p className="text-slate-500">Nessun ristorante</p> : <><FiscalBanner /><Outlet /></>}</main>
      </div>
    </div>
  );
}
